import z from 'zod'
import { createClient } from './createClient'

/** userRole API の response schema */
const userRoleResponseSchema = z.object({
  /** アドレスの種別 (user / agent / vault / subAccount / missing) */
  role: z.string(),
  data: z
    .object({
      /** role=agent の場合のマスターアカウントアドレス */
      user: z.string().optional(),
    })
    .optional(),
})

/** Hyperliquid アドレスの種別 */
export type HyperliquidUserRole =
  | {
      /** API Wallet (agent) のアドレス / 履歴照会には使えない */
      readonly role: 'agent'
      /** agent を承認したマスターアカウントのアドレス */
      readonly masterAddress: string | undefined
    }
  | {
      /** user / vault / subAccount / missing などの種別 */
      readonly role: string
    }

/**
 * Hyperliquid の userRole API でアドレスの種別を取得する。
 * API Wallet (agent) のアドレスを登録すると履歴照会が空になるため、登録前の検証に使う。
 * @param params.address - 検証するアドレス
 * @param params.createHeaders - request headers 作成関数 (proxy の認証用)
 * @param params.proxy - request を proxy する場合の proxy base URL
 * @returns アドレスの種別 / agent の場合はマスターアカウントのアドレスを含む
 */
export const getHyperliquidUserRole = async ({
  address,
  createHeaders,
  proxy,
}: {
  address: string
  createHeaders?: () => Promise<Record<string, string>>
  proxy?: string
}): Promise<HyperliquidUserRole> => {
  const client = createClient({
    apiKey: address,
    createHeaders,
    proxy,
  })
  const response = await client.run({
    method: 'POST',
    path: '/info',
    body: {
      type: 'userRole',
    },
    responseSchema: userRoleResponseSchema,
  })
  if (response.role === 'agent') {
    return {
      role: 'agent',
      masterAddress: response.data?.user,
    }
  }
  return { role: response.role }
}
